import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { Children, Fragment, isValidElement, useId } from 'react';
import { Modal as CosmaModal } from 'is24-corecss';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import { Header } from './Header';
import { Footer } from './Footer';
import { Content } from './Content';
import { Title } from './components/Title';
import { Description } from './components/Description';
import { SlotContext } from './SlotContext';
import { styles } from './styles';

export const Modal = ({
  isVisible,
  onClose,
  onDismiss = onClose,
  compact = false,
  viewportHeight = true,
  width = compact ? 480 : 1000,
  children,
  className = '',
}: {
  isVisible: boolean;
  onClose: () => void;
  onDismiss?: () => void;
  compact?: boolean;
  viewportHeight?: boolean;
  width?: ComponentProps<typeof CosmaModal.Surface>['width'];
  children: ReactNode;
  className?: string;
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const headerId = useId();
  const footerId = useId();
  useBodyScroll(isVisible);
  useModalFocus(isVisible);
  useElementDarkMode('[data-testid="modal-backdrop"]', isVisible);
  const slots = flattenFragments(children);
  const header = slots.find((child) => child.type === Header);
  const content = slots.find((child) => child.type === Content);
  const footer = slots.find((child) => child.type === Footer);
  const hasTitle = contains(children, Title);
  const hasDescription = contains(children, Description);

  return (
    <SlotContext.Provider value={{ headerId, footerId, titleId, descriptionId, compact }}>
      <CosmaModal.Root isOpen={isVisible} onClose={onDismiss} fullHeight={compact}>
        <CosmaModal.Backdrop className={compact ? styles.compactBackdrop : undefined}>
          <CosmaModal.Surface
            width={width}
            aria-labelledby={hasTitle ? titleId : header ? headerId : undefined}
            aria-describedby={hasDescription ? descriptionId : footer ? footerId : undefined}
            className={`display-flex ${compact ? '' : 'overflow-hidden'} ${styles.container} ${styles.surface} ${compact ? styles.compactSurface : !viewportHeight ? styles.contentSized : ''} ${className}`}
          >
            {header}
            {content}
            {footer}
          </CosmaModal.Surface>
        </CosmaModal.Backdrop>
      </CosmaModal.Root>
    </SlotContext.Provider>
  );
};

const flattenFragments = (children: ReactNode): ReactElement<{ children?: ReactNode }>[] =>
  Children.toArray(children).flatMap((child) => {
    if (!isValidElement<{ children?: ReactNode }>(child)) {
      return [];
    }
    return child.type === Fragment ? flattenFragments(child.props.children) : [child];
  });

const contains = (children: ReactNode, type: unknown): boolean =>
  flattenFragments(children).some((child) => child.type === type || contains(child.props.children, type));
